/**
 * Renders predicted-trajectory vectors for feed tracks, decoupled from the
 * marker pipeline: it reads the same plugin data + layer visibility, projects
 * each moving track forward, and draws thin lines. Re-runs on data, look-ahead,
 * visibility, and view-mode changes.
 */

import { useEffect } from 'react';
import { projectPosition, type Track } from '@vtak/shared';
import type { MapInstance, ViewMode } from '@/map/map-setup';
import { usePluginDataStore } from '@/stores/plugin-data-store';
import { useLayerVisibilityStore } from '@/stores/layer-visibility-store';
import { useTimelineStore } from '@/stores/timeline-store';
import { useMapStore } from '@/stores/map-store';
import { useTrajectoryStore } from '@/stores/trajectory-store';
import { trackKinematics } from '@/map/trajectory/track-kinematics';
import {
  renderTrajectories,
  clearTrajectories,
  type TrajectorySegment,
} from '@/map/trajectory/trajectory-render';
import { getAffiliationColor } from '@/map/marker-icons';

/** Delay before repainting after a 2D↔3D switch (matches the map recreate). */
const VIEW_SWITCH_REPAINT_MS = 850;

/** Subscribe trajectory vectors to the map. Call once alongside useTrackLayers. */
export function useTrajectoryLayers(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): void {
  useEffect(() => {
    const run = (): void => recompute(mapRef, modeRef);
    run();

    const unsubData = usePluginDataStore.subscribe(run);
    const unsubTraj = useTrajectoryStore.subscribe(run);
    const unsubVis = useLayerVisibilityStore.subscribe(run);
    const unsubMode = subscribeViewMode(run);

    return () => {
      unsubData();
      unsubTraj();
      unsubVis();
      unsubMode();
      clearTrajectories(modeRef.current);
    };
  }, []);
}

/** Recompute and render trajectory segments for all enabled, visible layers. */
function recompute(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): void {
  const map = mapRef.current;
  if (!map) return;

  const mode = modeRef.current;
  const { enabledLayers, lookaheadMinutes } = useTrajectoryStore.getState();

  if (enabledLayers.size === 0 || useTimelineStore.getState().mode === 'historical') {
    clearTrajectories(mode);
    return;
  }

  renderTrajectories(map, mode, collectSegments(enabledLayers, lookaheadMinutes * 60));
}

/** Build a trajectory segment for every moving track in the enabled layers. */
function collectSegments(enabledLayers: Set<string>, seconds: number): TrajectorySegment[] {
  const visibility = useLayerVisibilityStore.getState();
  const data = usePluginDataStore.getState().data;
  const segments: TrajectorySegment[] = [];

  for (const layerId of enabledLayers) {
    if (!visibility.isVisible(layerId)) continue;
    const tracks = data.get(layerId) as Track[] | undefined;
    if (!tracks) continue;
    for (const track of tracks) appendSegment(segments, track, seconds);
  }
  return segments;
}

/** Project one track forward and push its segment, skipping stationary tracks. */
function appendSegment(segments: TrajectorySegment[], track: Track, seconds: number): void {
  const kinematics = trackKinematics(track);
  if (!kinematics) return;

  const projected = projectPosition(
    { lat: track.lat, lon: track.lon },
    kinematics.headingDeg,
    kinematics.speedMs,
    seconds,
  );
  segments.push({
    uid: track.uid,
    from: { lat: track.lat, lng: track.lon },
    to: { lat: projected.lat, lng: projected.lon },
    color: getAffiliationColor(track.type),
  });
}

/** Clear on a 2D↔3D switch, then repaint once the new map is ready. */
function subscribeViewMode(run: () => void): () => void {
  let previousMode = useMapStore.getState().viewMode;
  let pending: ReturnType<typeof setTimeout> | null = null;

  return useMapStore.subscribe((state) => {
    if (state.viewMode === previousMode) return;
    clearTrajectories(previousMode);
    previousMode = state.viewMode;
    if (pending) clearTimeout(pending);
    pending = setTimeout(run, VIEW_SWITCH_REPAINT_MS);
  });
}
